import { useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import * as api from '../../../api';
import * as store from '../../../reducers/store';
import * as logger from '../../../logger';

import Loader from '../../../components/Loader';
import { FreehandBodyDiagramPageEditViewContainer } from './FreehandBodyDiagramPageEditView';
import FreehandBodyDiagramPageExistView from './FreehandBodyDiagramPageExistView';

import styles from './index.module.css';

interface Props extends WithTranslation {
  navigateToNextPage: () => Promise<void>;
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  setFormField: (formFieldId: string, value: string) => void;
  encounter: api.Encounter.Encounter;
}

const FreehandBodyDiagramPage: React.FC<Props> = ({
  navigateToNextPage,
  field,
  setFormField,
  encounter,
  t,
}) => {
  const theme = useSelector((state: store.All) => state.theme);
  const [existingImageDataUri, setExistingImageDataUri] = useState<string>();
  const [editing, setEditing] = useState(true);
  const [pending, setPending] = useState<boolean>(!!field.value);

  useEffect(() => {
    const downloadExisting = async () => {
      if (field.value) {
        try {
          const { status, dataURL } =
            await api.client.PatientUpload.downloadImageFile(field.value);
          if (status === 200 && dataURL) {
            setExistingImageDataUri(dataURL);
            setEditing(false);
          }
        } catch (error) {
          logger.error(error);
        } finally {
          setPending(false);
        }
      }
    };
    downloadExisting();
  }, [field.value, setExistingImageDataUri]);

  if (pending) return <Loader />;
  return (
    <>
      <div className={styles.LabelStyle}>
        {t('Please shade all locations you have pain or discomfort.')}
      </div>
      {editing ? (
        <FreehandBodyDiagramPageEditViewContainer
          navigateToNextPage={navigateToNextPage}
          field={field}
          setFormField={setFormField}
          encounter={encounter}
          brushColor={theme?.primaryHexColor}
        />
      ) : (
        existingImageDataUri && (
          <FreehandBodyDiagramPageExistView
            imageSrc={existingImageDataUri}
            onContinue={navigateToNextPage}
            onEdit={() => setEditing(true)}
          />
        )
      )}
    </>
  );
};

export default withTranslation()(FreehandBodyDiagramPage);
